import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronRight, ShoppingBag, MessageCircle, ArrowRight, Check } from "lucide-react";
import { CATEGORY_LABELS, ANIMAL_LABELS, formatPrice, effectivePrice } from "@/data/products";
import { getPublicProductBySlug, getRecipesByProductSlug } from "@/lib/public.functions";
import { AnimalIcon, CategoryIcon } from "@/components/icons/BrandIcons";
import { ProductCard } from "@/components/ProductCard";
import { Eyebrow } from "@/components/Eyebrow";
import { Reveal } from "@/components/Reveal";
import { useBooking } from "@/store/booking";
import { cn } from "@/lib/utils";
import { useProductsHidden, useRecipesHidden } from "@/hooks/useSiteSettings";
import { assertSectionVisible } from "@/lib/visibility";

export const Route = createFileRoute("/prodotti/$slug")({
  beforeLoad: () => assertSectionVisible("hide_products"),
  loader: async ({ params }) => {
    const { product, related } = await getPublicProductBySlug({ slug: params.slug });
    if (!product) throw notFound();
    return { product, related };
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? [
          { title: `${loaderData.product.name} — La Bottega delle Carni da Marco` },
          { name: "description", content: loaderData.product.shortDescription },
          { property: "og:title", content: loaderData.product.name },
          { property: "og:description", content: loaderData.product.shortDescription },
          { property: "og:image", content: loaderData.product.image },
          { property: "og:url", content: `https://labottegadellecarnidamarco.it/prodotti/${loaderData.product.slug}` },
        ]
      : [],
    links: loaderData ? [{ rel: "canonical", href: `https://labottegadellecarnidamarco.it/prodotti/${loaderData.product.slug}` }] : [],
    scripts: loaderData
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              name: loaderData.product.name,
              description: loaderData.product.shortDescription,
              image: loaderData.product.image,
              url: `https://labottegadellecarnidamarco.it/prodotti/${loaderData.product.slug}`,
              brand: { "@type": "Brand", name: "La Bottega delle Carni da Marco" },
              offers: {
                "@type": "Offer",
                priceCurrency: "EUR",
                ...(effectivePrice(loaderData.product) > 0
                  ? { price: effectivePrice(loaderData.product) }
                  : {}),
                availability: "https://schema.org/InStock",
                url: `https://labottegadellecarnidamarco.it/prodotti/${loaderData.product.slug}`,
                seller: { "@type": "Organization", name: "La Bottega delle Carni da Marco" },
              },
            }),
          },
        ]
      : [],
  }),
  notFoundComponent: () => (
    <div className="min-h-[50vh] flex items-center justify-center pt-20">
      <div className="text-center">
        <h1 className="font-display text-4xl">Prodotto non trovato</h1>
        <Link to="/prodotti" className="mt-4 inline-block text-brand-red">Torna al catalogo</Link>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => <div className="p-20 text-center">{error instanceof Error ? error.message : String(error)}</div>,
  component: ProductPage,
});

function ProductPage() {
  const productsHidden = useProductsHidden();
  const recipesHidden = useRecipesHidden();
  const navigate = useNavigate();
  useEffect(() => {
    if (productsHidden) navigate({ to: "/", replace: true });
  }, [productsHidden, navigate]);
  if (productsHidden) return null;
  const { product, related } = Route.useLoaderData() as {
    product: import("@/data/products").Product;
    related: import("@/data/products").Product[];
  };
  const gallery = product.gallery ?? [product.image];
  const [active, setActive] = useState(0);
  const add = useBooking((s) => s.add);
  const isBookable = product.bookable !== false;

  const { data: recipesData } = useQuery({
    queryKey: ["recipes-by-product", product.slug],
    queryFn: () => getRecipesByProductSlug({ slug: product.slug }),
  });
  const linkedRecipes = recipesData?.recipes ?? [];

  const onBook = () => {
    add({ slug: product.slug, name: product.name, price: effectivePrice(product), unit: product.unit, image: product.image });
    toast.success("Aggiunto al carrello richieste", { description: product.name });
  };

  return (
    <>
      {/* BREADCRUMB */}
      <div className="container mx-auto px-4 pt-14 pb-6">
        <motion.nav
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-xs text-brand-warm-gray flex items-center gap-1 flex-wrap"
        >
          <Link to="/" className="hover:text-brand-red transition-colors">Home</Link>
          <ChevronRight size={12}/>
          <Link to="/prodotti" className="hover:text-brand-red transition-colors">Prodotti</Link>
          <ChevronRight size={12}/>
          <span>{CATEGORY_LABELS[product.category]}</span>
          <ChevronRight size={12}/>
          <span className="text-brand-charcoal">{product.name}</span>
        </motion.nav>
      </div>

      {/* MAIN: image left, info right */}
      <section className="container mx-auto px-4 grid lg:grid-cols-2 gap-10 lg:gap-16 pb-12">
        {/* GALLERY */}
        <Reveal x={-24} y={0} duration={0.9}>
          <div className="relative aspect-square overflow-hidden rounded-md bg-brand-cream-dark zoom-img-parent shadow-[var(--shadow-soft)]">
            <motion.img
              key={gallery[active]}
              src={gallery[active]}
              alt={product.name}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="w-full h-full object-cover"
            />
            {product.featured && (
              <span className="absolute top-4 left-4 bg-brand-gold text-brand-charcoal text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full font-medium">In evidenza</span>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-3">
              {gallery.map((g: string, i: number) => (
                <motion.button
                  key={i}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setActive(i)}
                  className={cn(
                    "aspect-square overflow-hidden rounded-sm border-2 transition-all",
                    i === active ? "border-brand-red shadow-md" : "border-transparent opacity-60 hover:opacity-100"
                  )}
                >
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </motion.button>
              ))}
            </div>
          )}
        </Reveal>

        {/* INFO */}
        <Reveal x={24} y={0} duration={0.9} delay={0.1}>
          <Eyebrow>{CATEGORY_LABELS[product.category]}</Eyebrow>
          <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight text-brand-charcoal">
            {product.name}
          </h1>

          {/* Iconcine */}
          <div className="flex flex-wrap items-center gap-3 mt-5">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-cream-dark/50 text-xs uppercase tracking-[0.14em] text-brand-charcoal">
              <AnimalIcon animal={product.animal} size={16} />
              {ANIMAL_LABELS[product.animal]}
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-cream-dark/50 text-xs uppercase tracking-[0.14em] text-brand-charcoal">
              <CategoryIcon category={product.category} size={16} />
              {CATEGORY_LABELS[product.category]}
            </span>
            {isBookable && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-green/10 text-brand-green text-xs uppercase tracking-[0.14em]">
                <Check size={13} /> Disponibile su richiesta
              </span>
            )}
          </div>

          {/* Descrizione breve */}
          <p className="mt-6 text-brand-charcoal/85 leading-relaxed text-lg font-italic-serif italic">
            {product.shortDescription}
          </p>

          <div className="gold-divider my-7" />

          {/* Descrizione lunga */}
          <div className="space-y-4 text-sm text-brand-warm-gray leading-[1.85]">
            {product.longDescription.map((p: string, i: number) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                {p}
              </motion.p>
            ))}
          </div>
        </Reveal>
      </section>

      {/* PREZZO + CTA — sotto il blocco foto/info */}
      <section className="container mx-auto px-4 pb-16">
        <Reveal y={20} duration={0.7}>
          <div className="relative overflow-hidden rounded-md border border-brand-gold/30 bg-gradient-to-br from-brand-cream to-brand-cream-dark/40 p-6 md:p-8 shadow-[var(--shadow-soft)]">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-warm-gray">Prezzo {product.unit === "kg" ? "al kg" : "al pezzo"}</div>
                <div className="font-numeric font-bold text-brand-red text-4xl md:text-5xl mt-1">
                  {formatPrice(product)}
                </div>
                <div className="text-xs text-brand-warm-gray mt-2">
                  Prezzo orientativo · disponibilità verificata in bottega
                </div>
                <div className="text-xs text-brand-warm-gray mt-1">
                  Allergeni e origine della carne: te li indichiamo al banco o quando ti richiamiamo.
                </div>
              </div>
              {isBookable ? (
                <motion.button
                  whileHover={{ y: -2, boxShadow: "0 12px 30px -10px rgba(196,53,42,0.5)" }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  onClick={onBook}
                  className="group inline-flex items-center justify-center gap-3 bg-brand-red hover:bg-brand-red-hover text-white px-8 py-4 rounded-md text-sm uppercase tracking-[0.18em] font-medium"
                >
                  <ShoppingBag size={16} />
                  Aggiungi alla richiesta
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </motion.button>
              ) : (
                <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}>
                  <Link
                    to="/contatti"
                    className="group inline-flex items-center justify-center gap-3 bg-brand-charcoal hover:bg-brand-red text-white px-8 py-4 rounded-md text-sm uppercase tracking-[0.18em] font-medium transition-colors"
                  >
                    <MessageCircle size={16} />
                    Richiedi ora
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                </motion.div>
              )}
            </div>
          </div>
        </Reveal>
      </section>

      {/* CARATTERISTICHE */}
      {product.specs.length > 0 && (
        <section className="container mx-auto px-4 pb-20">
          <Reveal>
            <h2 className="font-display text-3xl text-brand-charcoal">Caratteristiche</h2>
            <div className="gold-divider mt-4 max-w-[80px]" />
          </Reveal>
          <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-1 mt-8 text-sm">
            {product.specs.map((s: { label: string; value: string }, i: number) => (
              <motion.div
                key={`${s.label}-${i}`}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className={cn(
                  "gap-4 border-b border-brand-charcoal/10 py-3",
                  s.label ? "flex justify-between" : "block"
                )}
              >
                {s.label && (
                  <dt className="text-brand-warm-gray uppercase tracking-[0.12em] text-xs">{s.label}</dt>
                )}
                <dd className={cn("text-brand-charcoal", s.label ? "text-right" : "text-left")}>{s.value}</dd>
              </motion.div>
            ))}
          </dl>
        </section>
      )}

      {/* CORRELATI */}
      {related.length > 0 && (
        <section className="bg-brand-cream-dark/40 py-16">
          <div className="container mx-auto px-4">
            <Reveal>
              <h2 className="font-display text-3xl mb-8">Potrebbero interessarti</h2>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.08} y={20}>
                  <ProductCard product={p} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RICETTE CONSIGLIATE */}
      {!recipesHidden && linkedRecipes.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <Reveal>
              <Eyebrow>Dal ricettario</Eyebrow>
              <h2 className="font-display text-3xl mt-3 mb-8">Ricette consigliate con questo prodotto</h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {linkedRecipes.map((r, i) => (
                <Reveal key={r.slug} delay={i * 0.06} y={20}>
                  <Link
                    to="/ricette/$slug"
                    params={{ slug: r.slug }}
                    className="group block rounded-md overflow-hidden bg-white shadow-[var(--shadow-soft)] hover:shadow-lg transition-shadow"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-brand-cream-dark zoom-img-parent">
                      {r.cover && (
                        <img src={r.cover} alt={r.title} className="zoom-img w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="p-5">
                      <div className="text-[11px] uppercase tracking-[0.18em] text-brand-warm-gray">{r.category}</div>
                      <h3 className="font-display text-xl mt-2 text-brand-charcoal group-hover:text-brand-red transition-colors">{r.title}</h3>
                      {r.excerpt && <p className="text-sm text-brand-warm-gray mt-2 line-clamp-2">{r.excerpt}</p>}
                      <div className="mt-3 inline-flex items-center gap-1 text-brand-red text-xs uppercase tracking-[0.16em]">
                        Leggi la ricetta <ArrowRight size={12} />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
